import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QueryLayerServiceConfigurationDc } from "@evergis/api";

import { formatDataSourceCondition } from "../../Dashboard/utils";
import { getFeatureDataSourcesAreLoading, getFeatureDataSources } from "../../../ducks/showFeature/selectors";
import { setFeatureDataSourcesAreLoading, setFeatureDataSources } from "../../../ducks/showFeature/showFeature";
import { useLayerParams } from "../../../hooks/eql/useLayerParams";
import { useFeatureCardModule } from "./useFeatureCardModule";
import { useDataSources } from "../../Dashboard/hooks/useDataSources";
import { LayerInfo } from "../../../interfaces/resourceInfo";
import { FeatureAttribute } from "../components/Attributes/FeatureAttributeField";
import { ConfigDataSource, ConfigFilter, DataSourcePromise, SelectedFilters } from "../types";
import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";

export const useFeatureDataSources = (
  layerInfo: LayerInfo,
  attributes: FeatureAttribute[],
  filters: SelectedFilters,
) => {
  const dispatch = useDispatch();
  const featureDataSources = useSelector(getFeatureDataSources);
  const dataSourcesAreLoading = useSelector(getFeatureDataSourcesAreLoading);
  const { feature } = useFeatureCardModule();
  const [layerParams] = useLayerParams(layerInfo);
  const config = layerInfo?.configuration.cardConfiguration;
  const { eqlParameters } = layerInfo?.configuration as QueryLayerServiceConfigurationDc;
  const dataSources: ConfigDataSource[] = config?.dataSources;
  const configFilters: ConfigFilter[] = config?.filters;

  const filtersDeps = useMemo(() => JSON.stringify(filters), [filters]);

  const { getDataSourcePromises, getUpdatingDataSources, getUpdatedDataSources } = useDataSources({
    filters,
    config,
    attributes,
    layerParams,
    eqlParameters,
  });

  const prevFeature = useRef<FeatureDcExtended>(null);
  const prevLayerName = useRef("");

  const fetchData = useCallback(
    (fetchingDataSources?: ConfigDataSource[]) => {
      if (dataSourcesAreLoading || !layerInfo?.name || !feature?.id) return;

      const currentDataSources = (fetchingDataSources || dataSources)?.filter(({ condition }) => {
        return (
          !condition ||
          formatDataSourceCondition({
            condition,
            configFilters,
            filters,
            attributes,
            layerParams,
            eqlParameters,
          })
        );
      });

      if (!currentDataSources?.length) return;

      dispatch(setFeatureDataSourcesAreLoading(true));

      const otherDataSources =
        featureDataSources?.length && fetchingDataSources?.length
          ? featureDataSources.filter(({ name }) => !currentDataSources.some(item => item.name === name))
          : [];

      if (otherDataSources?.length) {
        dispatch(setFeatureDataSources(otherDataSources));
      }

      Promise.allSettled(currentDataSources.map(getDataSourcePromises))
        .then((responses: DataSourcePromise[]) => {
          dispatch(setFeatureDataSources(getUpdatedDataSources(responses, currentDataSources, otherDataSources)));
        })
        .finally(() => {
          dispatch(setFeatureDataSourcesAreLoading(false));
        });
    },
    [
      dataSourcesAreLoading,
      layerInfo?.name,
      feature?.id,
      dataSources,
      dispatch,
      featureDataSources,
      getDataSourcePromises,
      configFilters,
      filters,
      attributes,
      layerParams,
      eqlParameters,
      getUpdatedDataSources,
    ],
  );

  useEffect(() => {
    if (
      !feature?.id ||
      !layerInfo.name ||
      prevFeature.current?.id === feature.id ||
      (prevFeature.current?.id !== feature.id &&
        prevFeature.current?.layer !== feature.layer &&
        prevLayerName?.current === layerInfo.name)
    )
      return;

    prevFeature.current = feature;
    prevLayerName.current = layerInfo.name;

    dispatch(setFeatureDataSources(null));

    fetchData();
  }, [feature?.id, layerInfo?.name, layerParams]);

  useEffect(() => {
    fetchData(getUpdatingDataSources());
  }, [filtersDeps]);
};
